import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import { useState, useEffect, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBookOpen,
  faUsers,
  faEllipsisVertical,
  faArrowRightFromBracket,
  faCircleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { useHandleFile } from "../../../core/hooks/useHandleFile";
import Skeleton from "react-loading-skeleton";
import { useHandleCourses } from "../../../core/hooks/useHandleCourses";
import {
  Modal,
  Button,
  Popconfirm,
  message,
  Dropdown,
  Tooltip,
  Progress,
} from "antd";
import { useTranslation } from "react-i18next";
import type { MenuProps } from "antd";
import { useAuth } from "../../../core/hooks/useAuth";
import { useTrackList } from "../../../core/hooks/useTrackVideo";
import { extractTextAndUrls } from "../../../core/common/ults/domParser";

const CourseCard = ({
  course,
  index,
  isUnenrolled,
  onCourseClick,
  showEnrolledCount = true,
  progressPercent,
}: any) => {
  const navigate = useNavigate();
  const {
    courseDetails,
    selfEnroll,
    courseContents,
    unenrolCourseMutation,
    typeEnrol,
  } = useHandleCourses(course?.id);
  const courseDetailData = courseDetails?.courses?.[0];
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFailModalOpen, setIsFailModalOpen] = useState(false);
  const { t } = useTranslation();
  const { moodleUser } = useAuth()
  const { data: trackList } = useTrackList(course?.id, moodleUser);

  const normalizeUrl = (url: string) => {
    try {
      const u = new URL(decodeURIComponent(url));
      return u.origin + u.pathname;
    } catch {
      return url;
    }
  };

  const videoStatusMap = useMemo(() => {
    if (!trackList?.length) return {};
    return trackList.reduce((map: any, item: any) => {
      const cleanUrl = normalizeUrl(item.videoid);
      const ts = Math.floor(Number(item.playtime) || 0);
      const dur = Math.floor(Number(item.duration) || 0);
      let status = "notStarted";
      if (ts > 0 && ts < dur) status = "inProgress";
      else if (ts >= dur && dur > 0) status = "completed";
      const percent = dur > 0 ? Math.floor((ts / dur) * 100) : 0;
      map[cleanUrl] = { status, percent };
      return map;
    }, {});
  }, [trackList]);

  const getVideoInfo = (url: string) => {
    const key = normalizeUrl(url);
    const entry = (videoStatusMap as any)[key];
    if (!entry) return { status: "Chưa xem", percent: 0 };
    const label = entry.status === "completed" ? "Đã xem" : entry.status === "inProgress" ? "Đang xem" : "Chưa xem";
    return { status: label, percent: entry.percent };
  };

  const weekData = (courseContents || [])
    .filter((section: any) => section.section > 0)
    .map((section: any) => {
      const videos = section.modules?.flatMap((mod: any) => {
        const list: any[] = [];
        const pushVideo = (name: string, url: string) => {
          const info = getVideoInfo(url);
          list.push({ name, ...info });
        };
        if (mod.modname === "unilabel") {
          const result = extractTextAndUrls(mod.description);
          result?.items?.forEach((res: any) => {
            if (res.type === "video" || res.url?.includes(".mp4")) pushVideo(res.text, res.url);
          });
        } else if (mod.modname === "resource") {
          const fileUrl = mod.contents?.[0]?.fileurl;
          if (fileUrl?.includes(".mp4")) pushVideo(mod.name, fileUrl);
        } else if (mod.modname === "url") {
          const externalUrl = mod.contents?.[0]?.fileurl || mod.externalurl;
          if (externalUrl && (externalUrl.includes(".mp4") || externalUrl.includes("youtube") || externalUrl.includes("vimeo"))) {
            pushVideo(mod.name, externalUrl);
          }
        }
        return list;
      }) || [];
      return { videos };
    });

  const calculateWeekProgress = (videos: any[]) => {
    if (!videos || videos.length === 0) return 0;

    const total = videos.length;
    const completed = videos.filter((v: any) => v.status === "Đã xem").length;
    return Math.round((completed / total) * 100);
  };



  const totalWeeks = weekData?.filter((week: any) => week.videos.length > 0).length;
  const completedWeeks = weekData.filter(
    (week: any) => calculateWeekProgress(week.videos) === 100
  ).length;


  const theoryProgressPercent = totalWeeks > 0
    ? Math.round((completedWeeks / totalWeeks) * 100)
    : 0;


  const countModulesByType = (modname: string): number => {
    return courseContents
      ?.flatMap((section: any) => section.modules || [])
      ?.filter((module: any) => module.modname === modname)?.length || 0;
  };

  const countCompletedModulesByType = (modname: string): number => {
    return (
      courseContents
        ?.flatMap((section: any) => section.modules || [])
        ?.filter(
          (module: any) =>
            module.modname === modname &&
            [1, 2].includes(module.completiondata?.state)
        )?.length || 0
    );
  };

  const totalQuizModules = countModulesByType("quiz");
  const totalAssignModules = countModulesByType("assign");

  const completedQuiz = countCompletedModulesByType("quiz");
  const completedAssign = countCompletedModulesByType("assign");

  const quizProgressPercent = totalQuizModules
    ? Math.round((completedQuiz / totalQuizModules) * 100)
    : 0;

  const assignProgressPercent = totalAssignModules
    ? Math.round((completedAssign / totalAssignModules) * 100)
    : 0;

  const exerciseProgressPercent = Math.round(
    (assignProgressPercent + quizProgressPercent) / 2
  );


  const allModules = courseContents?.flatMap((section: any) => section.modules || []) || [];
  const countOtherModules = (): number => {
    return allModules.filter((mod: any) => mod.modname === 'forum' && mod.completion > 0).length;
  };

  const countCompletedOtherModules = (): number => {
    return allModules.filter(
      (mod: any) =>
        mod.modname === 'forum' &&
        [1, 2].includes(mod.completiondata?.state)
    ).length;
  };

  const otherProgressPercent = countOtherModules()
    ? Math.round((countCompletedOtherModules() / countOtherModules()) * 100)
    : 0;

  const fileUrl =
    courseDetailData?.overviewfiles?.[0]?.fileurl ||
    course?.overviewfiles?.[0]?.fileurl ||
    "";
  const { moodleFileSrc, isMoodleFileSrcLoading } = useHandleFile(fileUrl);

  const categories = courseDetailData?.categories || course?.categories;

  const [selectedItems, setSelectedItems] = useState(Array(4).fill(false));
  const [isHovered, setIsHovered] = useState(false);
  const [isUnenrollButtonHovered, setIsUnenrollButtonHovered] = useState(false);

  const getPlainText = (htmlString: string) => {
    if (!htmlString) return t("courses.noDescription");
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlString;
    return tempDiv.textContent?.trim() || "Chưa có mô tả";
  };

  const currentCourseEnrolmentTypeForDropdown = typeEnrol?.find(
    (enrolItem: any) => enrolItem.courseid === course?.id
  )?.enrolmenttype;
  const isSelfEnrolment = currentCourseEnrolmentTypeForDropdown === "self";

  const handleItemClick = (index: number) => {
    setSelectedItems((prevSelectedItems) => {
      const updatedSelectedItems = [...prevSelectedItems];
      updatedSelectedItems[index] = !updatedSelectedItems[index];
      return updatedSelectedItems;
    });
  };

  const handleClick = (e: React.MouseEvent) => {
    if (isUnenrolled) {
      e.preventDefault();
      setIsModalOpen(true);
      if (onCourseClick) {
        onCourseClick(course);
      }
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsFailModalOpen(false);
  };

  const handleEnroll = async () => {
    const response = await selfEnroll(course?.id);
    setIsModalOpen(false);
    if (response?.exception || response?.errorcode || response?.message) {
      setIsFailModalOpen(true);
    }
    if (response?.status === "true" || response?.status) {
      message.success(t("courses.enrollSuccess"));
      setIsModalOpen(false);
      navigate(`${all_routes.courseDetails}/${course?.id}`, {
        state: { course: course },
      });
      window.location.reload();
    }
  };

  const handleUnenroll = () => {
    unenrolCourseMutation.mutate(course?.id, {
      onSuccess: () => {
        message.success(t("courses.unenrollSuccess"));
        window.location.reload();
      },
      onError: () => {
        message.error(t("courses.unenrollFailure"));
      },
    });
  };

  const items: MenuProps["items"] = [
    {
      key: "unenroll",
      label: isSelfEnrolment ? (
        <Popconfirm
          title={t("courses.unenrollCourse")}
          description={t("courses.unenrollCourseConfirm")}
          onConfirm={handleUnenroll}
          okText={t("courses.agree")}
          cancelText={t("courses.cancel")}
        >
          <Button
            type="text"
            onMouseEnter={() => setIsUnenrollButtonHovered(true)}
            onMouseLeave={() => setIsUnenrollButtonHovered(false)}
            style={{ color: isUnenrollButtonHovered ? "red" : undefined }}
            onClick={(e) => e.stopPropagation()}
          >
            {t("courses.unenrollCourse")}{" "}
            <FontAwesomeIcon
              icon={faArrowRightFromBracket}
              style={{
                color: "blue",
                transition: "color 0.3s ease",
                marginLeft: 4,
              }}
            />
          </Button>
        </Popconfirm>
      ) : (
        <Tooltip
          title={
            <>
              {" "}
              <FontAwesomeIcon
                icon={faCircleExclamation}
                style={{ color: "red" }}
              />{" "}
              {t("courses.unenrollNotAllowed")}{" "}
            </>
          }
          placement="top"
          color="white"
          overlayInnerStyle={{ color: "black" }}
        >
          <span>
            <Button
              type="text"
              disabled
              style={{ color: "gray", cursor: "not-allowed" }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {t("courses.unenrollCourse")}{" "}
              <FontAwesomeIcon
                icon={faArrowRightFromBracket}
                style={{
                  color: "gray",
                  transition: "color 0.3s ease",
                  marginLeft: 4,
                }}
              />
            </Button>
          </span>
        </Tooltip>
      ),
    },
  ];

  const courseProgressPercent = Math.round(
    (theoryProgressPercent + otherProgressPercent + exerciseProgressPercent) / 3
  );

  return (
    <>
      <div className="course-item-two course-item mx-0 position-relative">
        {!isUnenrolled && showEnrolledCount && (
          <div
            className="position-absolute end-0 px-2 pt-1 top-0 text-2"
            style={{ zIndex: 10 }}
          >
            <Dropdown
              menu={{ items }}
              trigger={["click"]}
              placement="bottomRight"
            >
              <div>
                <FontAwesomeIcon
                  icon={faEllipsisVertical}
                  size="lg"
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                  style={{
                    color: isHovered ? "#FF4667" : "rgb(63 166 248)",
                    cursor: "pointer",
                    transition: "color 0.3s ease",
                  }}
                />
              </div>
            </Dropdown>
          </div>
        )}
        <div className="course-img d-flex flex-row">
          {isMoodleFileSrcLoading ? (
            <Skeleton />
          ) : (
            <Link
              to={`${all_routes.courseDetails}/${course?.id}`}
              state={{ course: course, percent: progressPercent }}
              onClick={handleClick}
            >
              <img
                src={moodleFileSrc || "/assets/img/course-default.png"}
                alt={
                  courseDetailData?.overviewfiles?.[0]?.filename ||
                  course?.overviewfiles?.[0]?.filename ||
                  "Course Image"
                }
                className="img-fluid"
                style={{ height: "170px", objectFit: "cover", width: "100%" }}
              />
            </Link>
          )}
        </div>
        <div className="course-content">
          <div className="d-flex flex-column justify-content-between mb-2">
            {categories?.map((category:any)=>(
              <span
              key={category.name}
              className="w-100 badge badge-light rounded-pill bg-light d-inline-flex fs-12 fw-medium mb-1"
              style={{
                wordWrap: "break-word",
                whiteSpace: "normal",
                maxWidth: "100%",
                textAlign: "left",
              }}
            >
              {category.name}
            </span>
            ))}
            <h6 className="title mb-2 ms-2 text-truncate">
              <Link
                to={`${all_routes.courseDetails}/${course?.id}`}
                state={{ course: course, percent: progressPercent }}
                onClick={handleClick}
              >
                <Tooltip title={course?.fullname}>{course?.fullname}</Tooltip>
              </Link>
            </h6>
            {progressPercent && (
              <Progress
                percent={courseProgressPercent}
                size="default"
                strokeColor={
                  courseProgressPercent === 100
                    ? "#52c41a" // 100% thì toàn bộ màu xanh
                    : {
                      "0%": "#ff4d4f",
                      "40%": "#faad14",
                      "90%": "#52c41a",
                    }
                }
              />
            )}
          </div>
          <div className="d-flex align-items-center justify-content-between">
            <div className="count d-flex flex-column ms-2">
              <div className="lesson-count d-flex align-items-center gap-2">
                <FontAwesomeIcon icon={faBookOpen} />
                <p>
                  {courseContents?.[courseContents.length - 1]?.section || 0}{" "}
                  {t("courses.weeks")}
                </p>
              </div>
              <div className="user-count d-flex align-items-center gap-2">
                {showEnrolledCount && (
                  <>
                    <FontAwesomeIcon icon={faUsers} />
                    <p>
                      {course?.enrolledusercount} {t("courses.students")}
                    </p>
                  </>
                )}
              </div>
            </div>
            <Link
              to={`${all_routes.courseDetails}/${course?.id}`}
              className="btn btn-dark btn-sm d-inline-flex align-items-center"
              state={{ course: course, percent: progressPercent }}
              onClick={handleClick}
            >
              {t("courses.viewCourse")}
              <i className="isax isax-arrow-right-3 ms-1" />
            </Link>
          </div>
        </div>
      </div>

      <Modal
        centered
        title={courseDetailData?.fullname || course?.fullname}
        open={isModalOpen}
        onCancel={handleCancel}
        footer={[
          <Popconfirm
            title={t("courses.enrollCourse")}
            description={t("courses.enrollCourseConfirm")}
            onConfirm={handleEnroll}
            okText={t("courses.agree")}
            cancelText={t("courses.cancel")}
          >
            <Button key="submit" type="primary">
              {t("courses.enrollCourse")}
            </Button>
          </Popconfirm>,
        ]}
      >
        <div className="course-info mt-3">
          <div className="pb-3 mb-3">
            <img
              src={moodleFileSrc || "/assets/img/course-default.png"}
              alt={
                courseDetailData?.overviewfiles?.[0]?.filename ||
                course?.overviewfiles?.[0]?.filename ||
                "Course Image"
              }
              className="img-fluid border rounded-2 mb-3"
            />
            <div className="">
              <h6 className="pb-2">{t("courses.courseInfo")}</h6>
              <p>
                {getPlainText(courseDetailData?.summary || course?.summary)}
              </p>
            </div>
          </div>

          <div className="mb-3 w-50 d-flex flex-row align-center gap-2">
            <h6>{t("courses.numberOfWeeks")}</h6>
            <p>{courseContents?.[courseContents.length - 1]?.section || 0}</p>
          </div>
        </div>
      </Modal>
      <Modal
        centered
        title={courseDetailData?.fullname || course?.fullname}
        open={isFailModalOpen}
        onCancel={handleCancel}
        footer={[
          <Button type="primary" onClick={handleCancel}>
            Cancel
          </Button>,
        ]}
      >
        <h6 className="text-danger">
          {t("courses.cannotEnrol")}
        </h6>
      </Modal>
    </>
  );
};

export default CourseCard;